import {
  NavigationProp,
  useIsFocused,
  useNavigation,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { LoyaltyEnrollmentPreferenceProvider } from "@/features/Loyalty/components/LoyaltyEnrollmentPreference/LoyaltyEnrollmentPreference";
import { whooshPatronUserIdentityForExternalTools } from "@/hooks/useIdentifyUserToExternalTools/types";
import { useIdentifyUserToExternalTools } from "@/hooks/useIdentifyUserToExternalTools/useIdentifyUserToExternalTools";
import { useInitializeConversionAnalytics } from "@/utils/Analytics/ConversionAnalytics/useInitializeConversionAnalytics";
import {
  readAnalyticsTrackingPermissionSession,
  useSyncAnalyticsTrackingPermission,
} from "@/utils/Analytics/useSyncAnalyticsTrackingPermission";
import { ClubCurrencyProvider } from "@whoosh/features/src/ClubCurrency";
import { ClubCurrencyContextFragment$key } from "@whoosh/features/src/ClubCurrency/__generated__/ClubCurrencyContextFragment.graphql";
import { ClubCurrencyContextUnauthenticatedFragment$key } from "@whoosh/features/src/ClubCurrency/__generated__/ClubCurrencyContextUnauthenticatedFragment.graphql";

import { currentProfileAtom } from "../../features/Club/atoms";
import {
  ActiveClubLanguageProvider,
  useSyncAuthenticatedLanguagePreference,
} from "../../features/LanguagePreference";
import {
  sessionCurrentClubAtom,
  sessionCurrentClubSlugAtom,
} from "../../features/Session";
import { BaseNavigatorParamList } from "../BaseNavigator/types";
import { ClubStackNavigatorStack } from "./ClubStackNavigator";
import {
  ClubNavigatorQuery,
  ClubNavigatorQuery$data,
} from "./__generated__/ClubNavigatorQuery.graphql";
import { useRegisterDeviceForPushNotificationsAsync } from "./useRegisterDeviceForPushNotificationsAsync";

type Props = {
  clubRef: PreloadedQuery<ClubNavigatorQuery>;
};
export const ClubNavigator = ({ clubRef }: Props) => {
  const isFocused = useIsFocused();
  const setSessionClubSlug = useSetAtom(sessionCurrentClubSlugAtom);

  const setSessionCurrentClub = useSetAtom(sessionCurrentClubAtom);
  const setCurrentProfile = useSetAtom(currentProfileAtom);
  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const data = usePreloadedQuery<ClubNavigatorQuery>(
    ClubNavigatorQueryString,
    clubRef
  );

  const user = data.session?.user;
  const analyticsTrackingAllowed = useSyncAnalyticsTrackingPermission(
    data.session
  );
  const authenticatedClub = data.session?.currentClientProfile?.member?.club;
  const unauthenticatedClub = data.unauthenticatedClub;
  const club = authenticatedClub || unauthenticatedClub;
  const clubCurrencyClubFragmentRef: ClubCurrencyContextFragment$key | null =
    authenticatedClub ?? null;
  const clubCurrencyUnauthenticatedFragmentRef: ClubCurrencyContextUnauthenticatedFragment$key | null =
    authenticatedClub ? null : unauthenticatedClub ?? null;
  const member = data.session?.currentClientProfile?.member;
  useSyncAuthenticatedLanguagePreference({
    active: isFocused,
    preferredLocale: user?.preferredLocale,
    userId: user?.id,
  });
  useInitializeConversionAnalytics(
    authenticatedClub?.appCustomization?.conversionAnalytics ??
      unauthenticatedClub?.conversionAnalytics,
    club?.id ?? club?.slug,
    analyticsTrackingAllowed
  );

  // A logged-out visitor to a private club must log in first. We let the Club
  // stack render only its Login route (see ClubStackNavigatorStack) rather than
  // imperatively navigating, so React Navigation owns the /login URL natively.
  const requiresLogin = !!club && !club.supportsPublic && !member;

  const hasSeenNotificationNudgeModal =
    user?.metadata?.hasSeenNotificationNudgeModal || false;

  const sessionClubValue = useMemo(() => {
    if (!club) return null;
    return {
      id: club.id ?? "",
      slug: club.slug,
      supportsPublic: club.supportsPublic,
      name: club.name,
      logoImage: club.logoImage?.publicId ?? "",
    };
  }, [club]);

  const currentProfileValue = useMemo(() => {
    if (!member?.id) return null;
    const ghinNumber = member.departmentMemberProfiles?.find(
      (dmp) => !!dmp?.ghinNumber
    )?.ghinNumber;
    return {
      memberId: member.id,
      membershipTypeId: member.membershipType?.id || "",
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      profileImagePublicId: member.profileImage?.publicId,
      memberGroups:
        member.memberGroups?.map((mg) => ({ id: mg?.id || "" })) || [],
      hasSeenNotificationNudgeModal,
      email: member.email ?? undefined,
      phoneNumber: member.phoneNumber?.nationalNumber,
      ghinNumber,
    };
  }, [
    member?.id,
    member?.membershipType?.id,
    member?.firstName,
    member?.lastName,
    member?.profileImage?.publicId,
    member?.memberGroups,
    member?.departmentMemberProfiles,
    member?.email,
    member?.phoneNumber?.nationalNumber,
    hasSeenNotificationNudgeModal,
  ]);

  const identifyUserToExternalTools = useIdentifyUserToExternalTools();

  useEffect(() => {
    if (!data.session?.user) {
      return;
    }

    const { identity, currentClub } = getUserIdentityForExternalTools(data);
    identifyUserToExternalTools({
      user: identity,
      currentClub,
    });
  }, [data, identifyUserToExternalTools]);

  useEffect(() => {
    if (sessionClubValue) {
      setSessionCurrentClub(sessionClubValue);
      setSessionClubSlug(sessionClubValue.slug);
    }
  }, [sessionClubValue, setSessionCurrentClub, setSessionClubSlug]);

  useEffect(() => {
    if (currentProfileValue) {
      setCurrentProfile(currentProfileValue);
    }
  }, [currentProfileValue, setCurrentProfile]);

  useEffect(() => {
    // A logged-out visitor to a private club is handled by the Club stack
    // rendering its Login route (requiresLogin). Here we only handle the case
    // where no club resolved for the slug at all.
    if (club) return;
    if (!member) {
      navigation.navigate("Unauthenticated");
    } else {
      navigation.navigate("NotFound");
    }
  }, [club, member, navigation]);

  useRegisterDeviceForPushNotificationsAsync({
    fragmentRef: data.session?.user || null,
  });

  const loyaltyEnrollmentScopeKey = [
    user?.id ?? "anonymous",
    club?.id ?? club?.slug ?? "unknown-club",
    member?.id ?? "anonymous-member",
  ].join(":");

  return club && data.session ? (
    <ActiveClubLanguageProvider active={isFocused} locale={club.defaultLocale}>
      <ClubCurrencyProvider
        clubFragmentRef={clubCurrencyClubFragmentRef}
        unauthenticatedClubFragmentRef={clubCurrencyUnauthenticatedFragmentRef}
      >
        <LoyaltyEnrollmentPreferenceProvider
          scopeKey={loyaltyEnrollmentScopeKey}
        >
          <ClubStackNavigatorStack
            sessionRef={data.session}
            requiresLogin={requiresLogin}
          />
        </LoyaltyEnrollmentPreferenceProvider>
      </ClubCurrencyProvider>
    </ActiveClubLanguageProvider>
  ) : null;
};

const getUserIdentityForExternalTools = (data: ClubNavigatorQuery$data) => {
  const analyticsTrackingSession = readAnalyticsTrackingPermissionSession(
    data.session
  );
  const user = data.session?.user;
  const currentProfile = data.session?.currentClientProfile;
  const currentClubNode = currentProfile?.member?.club;
  const activeClubs =
    user?.memberProfiles?.edges?.map((edge) => ({
      id: edge?.node?.member?.club?.id ?? "",
      name: edge?.node?.member?.club?.name ?? "",
    })) ?? [];

  const identity: whooshPatronUserIdentityForExternalTools = {
    userId: user?.id || undefined,
    email: user?.email || undefined,
    name: user?.username || undefined,
    activeClubs,
    requiresAnalyticsTrackingConsent:
      analyticsTrackingSession?.requiresAnalyticsTrackingConsent,
    analyticsTrackingConsentedAt:
      analyticsTrackingSession?.user?.__typename === "MemberUser"
        ? analyticsTrackingSession.user.metadata?.analyticsTrackingConsentedAt
        : null,
  };
  const currentClub = {
    id: currentClubNode?.id ?? "",
    name: currentClubNode?.name ?? "",
  };
  return {
    identity,
    currentClub,
  };
};

export const ClubNavigatorQueryString = graphql`
  query ClubNavigatorQuery($clubSlug: String!) {
    session {
      ...ClubStackNavigatorFragment_session
      ...useSyncAnalyticsTrackingPermission_session
      user {
        ...useRegisterDeviceForPushNotificationsAsyncFragment_memberUser
        id
        email
        username
        ... on MemberUser {
          preferredLocale
          metadata {
            hasSeenNotificationNudgeModal
          }
          memberProfiles(active: true) {
            edges {
              node {
                id
                member {
                  club {
                    id
                    name
                    slug
                    logoImage {
                      # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                      publicId: renderingKey
                    }
                  }
                }
              }
            }
          }
        }
      }
      currentClientProfile {
        member {
          ... on Member {
            id
            firstName
            lastName
            email
            phoneNumber {
              nationalNumber
            }
            membershipType {
              id
            }
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
            memberGroups {
              id
            }
            departmentMemberProfiles(type: GOLF) {
              ... on GolfMemberProfile {
                ghinNumber
                department {
                  slug
                }
              }
            }
          }
          ... on Customer {
            departmentMemberProfiles(type: GOLF) {
              ... on GolfMemberProfile {
                ghinNumber
                department {
                  slug
                }
              }
            }
            id
            email
            phoneNumber {
              nationalNumber
            }
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
            membershipType {
              id
            }
          }
          club {
            id
            name
            slug
            supportsPublic
            defaultLocale
            logoImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
            appCustomization {
              conversionAnalytics {
                enabled
                gtmContainerId
                ga4MeasurementId
                metaPixelId
              }
            }
            ...ClubCurrencyContextFragment
          }
        }
      }
    }
    unauthenticatedClub(slug: $clubSlug) {
      defaultLocale
      id
      name
      slug
      supportsPublic
      conversionAnalytics {
        enabled
        gtmContainerId
        ga4MeasurementId
        metaPixelId
      }
      logoImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
      ...ClubCurrencyContextUnauthenticatedFragment
    }
  }
`;
